import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Pagination,
  Stack,
  InputAdornment,
  Chip,
  Alert,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import GoogleIcon from '@mui/icons-material/Google';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LanguageIcon from '@mui/icons-material/Language';
import HelpIcon from '@mui/icons-material/Help';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import Api from '../../Services/Api';
import PageHeader from './PageHeader';
import RsocNavHeader from './RsocNavHeader';
import ImageGalleryDialog from './ImageGalleryDialog';

const RsocPagesList = () => {
  // State for filter and search
  const [domainId, setDomainId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Data state
  const [rsocPages, setRsocPages] = useState([]);
  const [domains, setDomains] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(null);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogExample, setDialogExample] = useState('');
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Image generation status state
  const [imageStatus, setImageStatus] = useState({});
  const [checkingImageStatus, setCheckingImageStatus] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPageData, setCurrentPageData] = useState(null);
  
  // State for tab in the unified dialog
  const [activeNetworkTab, setActiveNetworkTab] = useState(0);
  
  // Image gallery dialog state
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [selectedPageForGallery, setSelectedPageForGallery] = useState(null);
  
  // Network configurations for consistency across the component
  const networkConfigs = {
    gdn: {
      name: 'Google Display Network',
      shortName: 'Google Display',
      color: 'primary.main',
      icon: <GoogleIcon />,
      buttonColor: 'primary',
      parameters: [
        {
          name: 'ref_adnetwork=google display network',
          description: 'Static parameter that identifies the traffic source as Google Display Network'
        },
        {
          name: 'ref_pubsite={placement}',
          description: 'Dynamic parameter that will be replaced with the actual website URL where your ad appeared'
        },
        {
          name: 'ref_keyword={headline}',
          description: 'Dynamic parameter that will be replaced with the headline text from your ad'
        }
      ]
    },
    taboola: {
      name: 'Taboola',
      shortName: 'Taboola',
      color: '#FF3D00',
      icon: <LanguageIcon />,
      buttonColor: 'warning',
      parameters: [
        {
          name: 'ref_adnetwork=taboola',
          description: 'Static parameter that identifies the traffic source as Taboola'
        },
        {
          name: 'ref_pubsite={site_domain}',
          description: 'Dynamic parameter that will be replaced with the publisher\'s domain where your ad appeared'
        },
        {
          name: 'ref_keyword={title}',
          description: 'Dynamic parameter that will be replaced with the title of your ad'
        }
      ]
    },
    meta: {
      name: 'Meta',
      shortName: 'Meta',
      color: '#1877F2',
      icon: <FacebookIcon />,
      buttonColor: 'info',
      parameters: [
        {
          name: 'ref_adnetwork=meta',
          description: 'Static parameter that identifies the traffic source as Meta platforms'
        },
        {
          name: 'ref_pubsite=meta',
          description: 'Static parameter for Meta platforms (Facebook, Instagram)'
        },
        {
          name: 'ref_keyword={ad_name}',
          description: 'Dynamic parameter that will be replaced with the ad name from your Meta campaign'
        }
      ]
    }
  };
  
  // Debug: Monitor currentPageData changes
  useEffect(() => {
    if (currentPageData?.urls) {
      console.log('Current Page Data URLs:', currentPageData.urls);
    }
  }, [currentPageData]);
  
  // Fetch domains (for filter dropdown)
  const fetchDomains = async () => {
    setLoadingDomains(true);
    
    try {
      const response = await Api.searchDomains();
      
      if (response && response.data) {
        const mappedDomains = response.data.map(domain => ({
          id: domain.domain_name_id,
          name: domain.domain_name
        }));
        
        setDomains(mappedDomains);
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Could not load domains: ' + (err.message || 'Unknown error'));
    } finally {
      setLoadingDomains(false);
    }
  };
  
  // Fetch RSOC pages with optional filters
  const fetchRsocPages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {};
      
      if (domainId) {
        params.domain_name_id = domainId;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      // Add page parameter for pagination
      params.page = currentPage;
      
      const response = await Api.getRsocPages(params);
      
      if (response && response.rsoc_pages) {
        setRsocPages(response.rsoc_pages.data || []);
        setCurrentPage(response.rsoc_pages.current_page || 1);
        setTotalPages(response.rsoc_pages.last_page || 1);
      } else {
        setRsocPages([]);
        setError('No RSOC pages found');
      }
    } catch (err) {
      console.error('Error fetching RSOC pages:', err);
      setError('Error loading RSOC pages: ' + (err.message || 'Unknown error'));
      setRsocPages([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Load domains on mount
  useEffect(() => {
    fetchDomains();
  }, []);
  
  // Load RSOC pages on mount and when filters change
  useEffect(() => {
    fetchRsocPages();
  }, [domainId, currentPage]);
  
  // Check image status when pages are loaded
  useEffect(() => {
    if (rsocPages.length > 0) {
      checkAllImagesStatus();
    }
  }, [rsocPages]);
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      fetchRsocPages();
    }, 500); // Debounce for 500ms
    
    setSearchTimeout(timeout);
  };
  
  // Handle domain filter change
  const handleDomainChange = (e) => {
    setDomainId(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };
  
  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Process keywords into URL parameter format
  const formatKeywordsForUrl = (keywords) => {
    // Ensure keywords is an array
    const keywordsArray = Array.isArray(keywords) ? keywords : 
      (typeof keywords === 'string' ? 
        keywords.split(',').map(k => k.trim()) : 
        (keywords ? [keywords] : []));
    
    // Join keywords and replace spaces with %20
    return keywordsArray.length > 0 
      ? keywordsArray.map(k => String(k).replace(/ /g, '%20')).join(',') 
      : '';
  };

  // Generate URL for a specific ad network
  const generateAdNetworkUrl = (network, domain, slug, keywords) => {
    // Get raw keywords array before formatting
    let keywordsArray = [];
    if (Array.isArray(keywords)) {
      keywordsArray = keywords;
    } else if (typeof keywords === 'string') {
      keywordsArray = keywords.split(',').map(k => k.trim());
    } else if (keywords) {
      keywordsArray = [keywords];
    }
    
    // Get the first keyword for Meta URL, if available
    const firstKeyword = keywordsArray.length > 0 ? 
      String(keywordsArray[0]).replace(/ /g, '%20') : '';
    
    // Format all keywords for terms parameter
    const keywordsStr = formatKeywordsForUrl(keywords);
    const termsParam = keywordsStr ? `&terms=${keywordsStr}` : '';
    
    // Add rm=0 parameter to all URLs
    const rmParam = '&rm=0';
    
    // Network-specific tracking parameters
    const trackingParams = {
      gdn: {
        subid1: '&subid1={gclid}',
        subid2: '&subid2={campaignid}',
        subid3: '&subid3={placement}'
      },
      taboola: {
        subid1: '&subid1={click_id}',
        subid2: '&subid2={campaign_id}',
        subid3: '&subid3={site}'
      },
      meta: {
        subid1: '&subid1={{fbclid}}',
        subid2: '&subid2={{campaign.id}}',
        subid3: '&subid3={{ad.id}}'
      },
      standard: {
        subid1: '',
        subid2: '',
        subid3: ''
      }
    };
    
    // Get tracking params for the selected network
    const tracking = trackingParams[network] || trackingParams.standard;
    
    // Debug log to verify tracking params
    console.log(`Tracking params for ${network}:`, tracking);
    
    // URL configurations
    const urlConfigs = {
      standard: {
        url: `https://www.${domain}/${slug}/?rm=0${keywordsStr ? `&terms=${keywordsStr}` : ''}`,
        label: 'Page URL'
      },
      gdn: {
        url: `https://www.${domain}/${slug}/?ref_adnetwork=google%20display%20network&ref_pubsite={placement}&ref_keyword={headline}${termsParam}${rmParam}${tracking.subid1}${tracking.subid2}${tracking.subid3}`,
        label: networkConfigs.gdn.name
      },
      taboola: {
        url: `https://www.${domain}/${slug}/?ref_adnetwork=taboola&ref_pubsite={site_domain}&ref_keyword={title}${termsParam}${rmParam}${tracking.subid1}${tracking.subid2}${tracking.subid3}`,
        label: networkConfigs.taboola.name
      },
      meta: {
        url: `https://www.${domain}/${slug}/?ref_adnetwork=meta&ref_pubsite=meta&ref_keyword=${firstKeyword || '{ad_name}'}${termsParam}${rmParam}${tracking.subid1}${tracking.subid2}${tracking.subid3}`,
        label: networkConfigs.meta.name
      }
    };
    
    const config = urlConfigs[network] || urlConfigs.standard;
    
    // Debug log to verify final URL
    console.log(`Final ${network} URL:`, config.url);
    
    return config;
  };
  
  // Copy URL to clipboard
  const copyUrlToClipboard = (url, slug, label, network = '') => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopied(network ? `${network}-${slug}` : slug);
        setSuccess(`${label} copied to clipboard!`);
        setTimeout(() => {
          setCopied(null);
          setSuccess(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError(`Failed to copy ${label}`);
      });
  };
  
  // Copy standard page URL
  const copyPageUrl = (domain, slug, keywords = []) => {
    const { url, label } = generateAdNetworkUrl('standard', domain, slug, keywords);
    copyUrlToClipboard(url, slug, label);
  };
  
  // Generate and copy network-specific URL
  const copyNetworkUrl = (network, domain, slug, keywords = []) => {
    const { url, label } = generateAdNetworkUrl(network, domain, slug, keywords);
    copyUrlToClipboard(url, slug, label, network);
  };

  // Open URL generation menu
  const handleOpenMenu = (event, page, domain) => {
    setAnchorEl(event.currentTarget);
    setCurrentPageData({ ...page, domain });
  };
  
  // Close URL generation menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    // Don't reset currentPageData when closing menu to preserve data for the dialog
  };
  
  // Handle opening URL dialog directly
  const handleOpenUrlDialog = () => {
    if (!currentPageData) return;
    
    const domain = currentPageData.domain;
    const slug = currentPageData.slug;
    const keywords = currentPageData.keywords || [];
    
    if (!domain || !slug) {
      console.error('Missing required data for URL generation:', { domain, slug });
      setError('Missing domain or slug for URL generation');
      return;
    }
    
    // Generate all network URLs for dialog
    const networks = ['gdn', 'taboola', 'meta'];
    const urls = {};
    
    // Generate URLs with complete parameters
    networks.forEach(network => {
      const result = generateAdNetworkUrl(network, domain, slug, keywords);
      urls[network] = result.url;
      console.log(`Generated ${network} URL:`, result.url);
    });
    
    // Add detailed debug information
    console.log('Domain:', domain);
    console.log('Slug:', slug);
    console.log('Keywords:', keywords);
    console.log('Generated URLs for dialog:', urls);
    
    // Force a new state object to trigger re-render
    const updatedData = {
      ...currentPageData,
      domain,
      slug,
      keywords,
      urls,
      timestamp: Date.now() // Add timestamp to force state update
    };
    
    console.log('Updated page data:', updatedData);
    
    // Completely replace current page data
    setCurrentPageData(updatedData);
    
    // Reset tab to first tab and open dialog
    setActiveNetworkTab(0);
    setOpenDialog(true);
  };
  
  // Handler for menu item click
  const handleMenuItemClick = (action) => {
    if (!currentPageData) return;
    
    const domain = currentPageData.domain;
    const slug = currentPageData.slug;
    const keywords = currentPageData.keywords || [];
    
    if (action === 'gdn' || action === 'taboola' || action === 'meta') {
      copyNetworkUrl(action, domain, slug, keywords);
    } else if (action === 'url-info') {
      handleOpenUrlDialog();
    }
    
    handleCloseMenu();
  };
  
  // Handle tab change in the dialog
  const handleNetworkTabChange = (event, newValue) => {
    setActiveNetworkTab(newValue);
  };
  
  // Copy the URL from the unified dialog
  const copyUrlFromUnifiedDialog = (type) => {
    if (!currentPageData?.urls || !networkConfigs[type]) return;
    
    const url = currentPageData.urls[type];
    if (!url) return;
    
    const label = networkConfigs[type].name;
    
    navigator.clipboard.writeText(url)
      .then(() => {
        setSuccess(`${label} URL copied to clipboard!`);
        setTimeout(() => setSuccess(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError(`Failed to copy ${label} URL`);
      });
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    // Just close the dialog without clearing currentPageData
    setOpenDialog(false);
  };
  
  // Network tab content component for the dialog
  const NetworkTabContent = ({ network, url }) => {
    const config = networkConfigs[network];
    if (!config) return <Typography color="error">Invalid network configuration</Typography>;
    if (!url) return <Typography color="error">No URL available for {config.name}</Typography>;
    
    // Handle both string URLs and object URLs
    const templateUrl = typeof url === 'object' ? url.template : url;
    
    // Add descriptions for subid parameters
    const trackingParamDescriptions = {
      gdn: [
        {
          name: 'subid1={gclid}',
          description: 'Google Click ID for conversion tracking'
        },
        {
          name: 'subid2={campaignid}',
          description: 'Google Ads Campaign ID'
        },
        {
          name: 'subid3={placement}',
          description: 'Website placement where the ad appeared'
        }
      ],
      taboola: [
        {
          name: 'subid1={click_id}',
          description: 'Taboola Click ID for conversion tracking'
        },
        {
          name: 'subid2={campaign_id}',
          description: 'Taboola Campaign ID'
        },
        {
          name: 'subid3={site}',
          description: 'Publisher site information'
        }
      ],
      meta: [
        {
          name: 'subid1={{campaign.id}}',
          description: 'Meta Campaign ID for conversion tracking'
        },
        {
          name: 'subid2={{adset.id}}',
          description: 'Meta Ad Set ID'
        },
        {
          name: 'subid3={{ad.id}}',
          description: 'Meta Ad ID'
        }
      ]
    };
    
    // Get tracking param descriptions for this network
    const trackingParams = trackingParamDescriptions[network] || [];
    
    // Combine network parameters with tracking parameters
    const allParameters = [...config.parameters, ...trackingParams];
    
    return (
      <>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {config.name} URL:
        </Typography>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            bgcolor: '#f5f5f5', 
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            wordBreak: 'break-all',
            mb: 3,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            maxHeight: '150px',
            overflowY: 'auto'
          }}
        >
          {templateUrl}
        </Paper>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {config.name} Parameters:
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            These parameters help you track traffic from {config.name}.
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {allParameters.map((param, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color={config.color} gutterBottom>
                  {param.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {param.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color={config.color} gutterBottom>
                rm=0
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Parameter to disable right margin on the page
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Button 
          variant="contained" 
          color={config.buttonColor}
          startIcon={<ContentCopyIcon />}
          sx={{ mt: 3 }}
          onClick={() => copyUrlFromUnifiedDialog(network)}
        >
          Copy URL
        </Button>
      </>
    );
  };
  
  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = (page) => {
    setPageToDelete(page);
    setDeleteDialogOpen(true);
  };
  
  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPageToDelete(null);
  };
  
  // Handle deleting a page
  const handleDeletePage = async () => {
    if (!pageToDelete) return;
    
    setDeleting(true);
    
    try {
      const response = await Api.deleteRsocPage(pageToDelete.id);
      
      if (response && response.success) {
        setSuccess('RSOC page deleted successfully');
        // Refresh the list of pages
        fetchRsocPages();
      } else {
        setError('Failed to delete page: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting page:', err);
      setError('Error deleting page: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
      handleCloseDeleteDialog();
    }
  };
  
  // Handle regenerating images for a page
  const handleRegenerateImages = async (pageId, page) => {
    try {
      setImageStatus(prev => ({
        ...prev,
        [pageId]: { loading: true }
      }));
      
      // Pass title and slug to the API for better image generation
      const title = page.title;
      const slug = page.slug;
      
      const response = await Api.regenerateRsocPageImages(pageId, title, slug);
      
      if (response && response.success) {
        setSuccess(`Image generation started for "${title || 'this page'}"`);
        
        // Update status immediately to show pending
        setImageStatus(prev => ({
          ...prev,
          [pageId]: { hasImages: false, count: 0, loading: false, pending: true, slug: slug }
        }));
        
        // Check status periodically until images are generated
        const checkInterval = setInterval(async () => {
          try {
            const statusResponse = await Api.getRsocPageImageStatus(pageId, slug);
            
            if (statusResponse && statusResponse.success) {
              if (statusResponse.hasImages) {
                // Images are ready, clear interval and update status
                clearInterval(checkInterval);
                setImageStatus(prev => ({
                  ...prev,
                  [pageId]: {
                    hasImages: true,
                    count: statusResponse.imageCount || 0,
                    loading: false,
                    pending: false,
                    slug: statusResponse.slug,
                    realSlug: statusResponse.realSlug
                  }
                }));
                setSuccess('Images generated successfully!');
              }
            } else {
              // If error checking status, stop checking
              clearInterval(checkInterval);
              setImageStatus(prev => ({
                ...prev,
                [pageId]: { hasImages: false, count: 0, loading: false, error: true }
              }));
            }
          } catch (err) {
            // If error checking status, stop checking
            clearInterval(checkInterval);
            console.error('Error checking image status:', err);
            setImageStatus(prev => ({
              ...prev,
              [pageId]: { hasImages: false, count: 0, loading: false, error: true }
            }));
          }
        }, 5000); // Check every 5 seconds
        
        // Stop checking after 2 minutes (as a safeguard)
        setTimeout(() => {
          clearInterval(checkInterval);
        }, 120000);
      } else {
        setError('Failed to start image generation: ' + (response.message || 'Unknown error'));
        setImageStatus(prev => ({
          ...prev,
          [pageId]: { hasImages: false, count: 0, loading: false, error: true }
        }));
      }
    } catch (err) {
      console.error('Error regenerating images:', err);
      setError('Error regenerating images: ' + (err.message || 'Unknown error'));
      setImageStatus(prev => ({
        ...prev,
        [pageId]: { hasImages: false, count: 0, loading: false, error: true }
      }));
    }
  };

  // Generate video for page
  const handleGenerateVideo = async (page) => {
    try {
      setSuccess(null);
      setError(null);

      const response = await Api.generateRsocPageVideo(page.slug, page.title, 3);

      if (response && response.success) {
        setSuccess(response.message || `Video generation started for "${page.title}"`);
      } else {
        setError(response?.message || 'Failed to start video generation');
      }
    } catch (err) {
      console.error('Error generating video:', err);
      setError('Error generating video: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    }
  };

  // Menu items for URL actions
  const menuItems = [
    { action: 'gdn', label: `Copy ${networkConfigs.gdn.name} URL`, icon: networkConfigs.gdn.icon },
    { action: 'taboola', label: `Copy ${networkConfigs.taboola.name} URL`, icon: networkConfigs.taboola.icon },
    { action: 'meta', label: `Copy ${networkConfigs.meta.name} URL`, icon: networkConfigs.meta.icon },
    { divider: true },
    { action: 'url-info', label: 'View All URL Options', icon: <HelpIcon /> }
  ];
  
  // Fetch image status for a specific page
  const fetchImageStatus = async (pageId, slug = null) => {
    try {
      const response = await Api.getRsocPageImageStatus(pageId, slug);
      
      if (response && response.success) {
        setImageStatus(prev => ({
          ...prev,
          [pageId]: {
            hasImages: response.hasImages,
            count: response.imageCount || 0,
            loading: false,
            slug: response.slug,
            realSlug: response.realSlug
          }
        }));
      } else {
        setImageStatus(prev => ({
          ...prev,
          [pageId]: { hasImages: false, count: 0, loading: false }
        }));
      }
    } catch (err) {
      console.error('Error fetching image status:', err);
      setImageStatus(prev => ({
        ...prev,
        [pageId]: { hasImages: false, count: 0, loading: false, error: true }
      }));
    }
  };
  
  // Check image status for all pages
  const checkAllImagesStatus = async () => {
    if (checkingImageStatus || rsocPages.length === 0) return;
    
    setCheckingImageStatus(true);
    
    try {
      // Initialize status as loading for all pages
      const initialStatus = {};
      rsocPages.forEach(page => {
        initialStatus[page.id] = { loading: true };
      });
      setImageStatus(initialStatus);
      
      // Check each page's status
      for (const page of rsocPages) {
        // Pass both ID and slug to the API
        await fetchImageStatus(page.id, page.slug);
      }
    } catch (err) {
      console.error('Error checking all images status:', err);
    } finally {
      setCheckingImageStatus(false);
    }
  };
  
  // Handle opening image gallery
  const handleOpenImageGallery = (page) => {
    setSelectedPageForGallery(page);
    setImageGalleryOpen(true);
  };
  
  // Handle closing image gallery
  const handleCloseImageGallery = () => {
    setImageGalleryOpen(false);
    setSelectedPageForGallery(null);
  };
  
  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="RSOC Pages"
        subtitle="View and manage your RSOC pages for different domains"
      />
      
      <RsocNavHeader />
      
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="domain-filter-label">Filter by Domain</InputLabel>
              <Select
                labelId="domain-filter-label"
                value={domainId}
                label="Filter by Domain"
                onChange={handleDomainChange}
                disabled={loadingDomains}
              >
                <MenuItem value="">All Domains</MenuItem>
                {domains.map(domain => (
                  <MenuItem key={domain.id} value={domain.id}>
                    {domain.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by Title or Slug"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchRsocPages}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Loading...' : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Paper elevation={2} sx={{ p: 0, borderRadius: 1, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Keywords</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Images</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 1, fontSize: '0.875rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Loading RSOC pages...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rsocPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: 2
                    }}>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        No RSOC pages found
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {searchQuery || domainId ? 'Try adjusting your filters or ' : ''}
                        Create a new RSOC page to get started
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ mt: 1 }}
                        href="/rsoc/generator"
                        startIcon={<AddIcon />}
                      >
                        Create New Page
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rsocPages.map(page => (
                  <TableRow key={page.id} hover sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: 'background-color 0.2s ease',
                    height: '60px'
                  }}>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {page.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <Box component="span" sx={{ bgcolor: '#f8f8f8', px: 0.5, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {page.slug}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip 
                        label={domains.find(d => d.id === page.domain_name_id)?.name || `Domain ID: ${page.domain_name_id}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: '24px', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxWidth: '200px' }}>
                        {typeof page.keywords === 'string' 
                          ? page.keywords.split(',').slice(0, 3).map((keyword, i) => (
                              <Chip key={i} label={keyword.trim()} size="small" sx={{ height: '20px', fontSize: '0.65rem' }} />
                            ))
                          : Array.isArray(page.keywords)
                            ? page.keywords.slice(0, 3).map((keyword, i) => (
                                <Chip key={i} label={keyword} size="small" sx={{ height: '20px', fontSize: '0.65rem' }} />
                              ))
                            : <Typography variant="caption" color="text.secondary">No keywords</Typography>
                        }
                        {((typeof page.keywords === 'string' ? page.keywords.split(',') : page.keywords || []).length > 3) && (
                          <Chip label="+more" size="small" variant="outlined" sx={{ height: '20px', fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption">
                        {formatDate(page.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {imageStatus[page.id]?.loading ? (
                        <CircularProgress size={16} />
                      ) : imageStatus[page.id]?.hasImages ? (
                        <Tooltip title={`${imageStatus[page.id].count} images generated`}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: '16px', mr: 0.5 }} />
                            <Typography variant="caption">
                              {imageStatus[page.id].count}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : imageStatus[page.id]?.pending ? (
                        <Tooltip title="Image generation in progress">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={12} color="warning" sx={{ mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              Pending
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : imageStatus[page.id]?.error ? (
                        <Tooltip title="Error checking image status">
                          <ErrorIcon color="error" sx={{ fontSize: '16px' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="No images generated">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ImageIcon color="disabled" sx={{ fontSize: '16px', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              None
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => copyPageUrl(
                            domains.find(d => d.id === page.domain_name_id)?.name || '', 
                            page.slug,
                            page.keywords
                          )}
                          title="Copy URL"
                          sx={{ 
                            width: 28, height: 28,
                            bgcolor: 'rgba(25, 118, 210, 0.08)', 
                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' } 
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton 
                          color="primary" 
                          size="small"
                          title="View Page"
                          onClick={() => window.open(`https://www.${domains.find(d => d.id === page.domain_name_id)?.name || ''}/${page.slug}/`, '_blank')}
                          sx={{ 
                            width: 28, height: 28,
                            bgcolor: 'rgba(25, 118, 210, 0.08)', 
                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' } 
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                          color="primary"
                          size="small"
                          title="Generate Tracking URLs"
                          onClick={(e) => handleOpenMenu(e, page, domains.find(d => d.id === page.domain_name_id)?.name || '')}
                          sx={{ 
                            width: 28, height: 28,
                            bgcolor: 'rgba(25, 118, 210, 0.08)', 
                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' } 
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleRegenerateImages(page.id, page)}
                          disabled={imageStatus[page.id]?.loading}
                          title="Regenerate Images"
                          sx={{ 
                            width: 28, height: 28,
                            bgcolor: 'rgba(25, 118, 210, 0.08)', 
                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' } 
                          }}
                        >
                          <ImageIcon sx={{ fontSize: '16px' }} />
                          {imageStatus[page.id]?.loading && (
                            <CircularProgress
                              size={12}
                              sx={{
                                position: 'absolute',
                                color: 'primary.light'
                              }}
                            />
                          )}
                        </IconButton>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenImageGallery(page)}
                          disabled={!imageStatus[page.id]?.hasImages}
                          title="View Images Gallery"
                          sx={{
                            width: 28, height: 28,
                            bgcolor: imageStatus[page.id]?.hasImages
                              ? 'rgba(25, 118, 210, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                            '&:hover': {
                              bgcolor: imageStatus[page.id]?.hasImages
                                ? 'rgba(25, 118, 210, 0.12)'
                                : 'rgba(0, 0, 0, 0.08)'
                            },
                            opacity: imageStatus[page.id]?.hasImages ? 1 : 0.5
                          }}
                        >
                          <PhotoLibraryIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() => handleGenerateVideo(page)}
                          title="Generate Video Ads"
                          sx={{
                            width: 28, height: 28,
                            bgcolor: 'rgba(156, 39, 176, 0.08)',
                            '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.12)' }
                          }}
                        >
                          <VideoLibraryIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          title="Delete Page"
                          onClick={() => handleOpenDeleteDialog(page)}
                          sx={{ 
                            width: 28, height: 28,
                            bgcolor: 'rgba(211, 47, 47, 0.08)', 
                            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12)' } 
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                        {copied === page.slug && (
                          <Chip label="Copied!" size="small" color="success" variant="outlined" sx={{ height: '20px', fontSize: '0.65rem' }} />
                        )}
                        {copied === `gdn-${page.slug}` && (
                          <Chip label="GDN Copied!" size="small" color="success" variant="outlined" sx={{ height: '20px', fontSize: '0.65rem' }} />
                        )}
                        {copied === `taboola-${page.slug}` && (
                          <Chip label="Taboola Copied!" size="small" color="success" variant="outlined" sx={{ height: '20px', fontSize: '0.65rem' }} />
                        )}
                        {copied === `meta-${page.slug}` && (
                          <Chip label="Meta Copied!" size="small" color="success" variant="outlined" sx={{ height: '20px', fontSize: '0.65rem' }} />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {totalPages > 1 && (
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #e0e0e0' }}>
            <Pagination 
              count={totalPages} 
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
      
      {/* URL Generation Menu - Updated with Meta option */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {menuItems.map((item, index) => (
          item.divider ? 
            <Divider key={`divider-${index}`} />
          : (
            <MenuItem 
              key={`menu-item-${index}`}
              onClick={() => handleMenuItemClick(item.action)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          )
        ))}
      </Menu>
      
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Are you sure you want to delete this page?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This action cannot be undone. Are you sure you want to delete the page "{pageToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePage} 
            color="error" 
            variant="contained" 
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Image Gallery Dialog */}
      <ImageGalleryDialog
        open={imageGalleryOpen}
        onClose={handleCloseImageGallery}
        pageTitle={selectedPageForGallery?.title || ''}
        pageSlug={selectedPageForGallery?.slug || ''}
        pageId={selectedPageForGallery?.id || null}
      />
    </Container>
  );
};

export default RsocPagesList;